import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { parentApi } from '../api/parent'
import { Field } from './Field'

/**
 * Секция в карточке ученика для приглашения родителя:
 *  - свёрнута → кнопка «Пригласить родителя»
 *  - развёрнута → форма с выбором родства + создание ссылки
 *  - после успеха → копируемая invite-ссылка (действует 7 дней)
 */
export function ParentInviteSection({
  studentId,
  studentName,
}: {
  studentId: string
  studentName: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [relationship, setRelationship] = useState('parent')
  const invite = useMutation({
    mutationFn: () => parentApi.createInvite(studentId, relationship),
  })

  if (!open) {
    return (
      <div className="mt-4 mb-3 border-t border-gray-100 pt-3">
        <button
          onClick={() => setOpen(true)}
          className="text-sm text-primary hover:underline"
          type="button"
        >
          👨‍👧 {t('parentInviteSection.inviteButton', { name: studentName })}
        </button>
      </div>
    )
  }

  if (invite.data) {
    const link = `${window.location.origin}/parent-register?token=${encodeURIComponent(invite.data.token)}`
    return (
      <div className="mt-4 mb-3 border-t border-gray-100 pt-3 bg-green-50 border border-green-200 rounded-lg p-3">
        <div className="font-semibold text-sm text-green-900 mb-2">✓ {t('parentInviteSection.created')}</div>
        <p className="text-xs text-gray-700 mb-2">
          {t('parentInviteSection.shareHint')}
        </p>
        <input
          readOnly
          className="input-field text-xs font-mono break-all select-all"
          value={link}
          onFocus={(e) => e.currentTarget.select()}
        />
        <button
          type="button"
          onClick={() => { navigator.clipboard.writeText(link) }}
          className="mt-2 btn-outline text-xs"
        >
          📋 {t('parentInviteSection.copyLink')}
        </button>
      </div>
    )
  }

  return (
    <div className="mt-4 mb-3 border-t border-gray-100 pt-3 bg-primary-soft/30 border border-primary/20 rounded-lg p-3">
      <div className="font-semibold text-sm text-gray-900 mb-2">{t('parentInviteSection.title')}</div>
      <Field label={t('parentInviteSection.relationshipLabel')}>
        <select
          className="input-field text-sm"
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
        >
          <option value="mother">{t('parentInviteSection.mother')}</option>
          <option value="father">{t('parentInviteSection.father')}</option>
          <option value="guardian">{t('parentInviteSection.guardian')}</option>
          <option value="parent">{t('parentInviteSection.parentOther')}</option>
        </select>
      </Field>
      {invite.error && (
        <div className="text-xs text-red-600 mb-2">{(invite.error as Error).message}</div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="btn-outline text-xs"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => invite.mutate()}
          disabled={invite.isPending}
          className="btn-primary text-xs"
        >
          {invite.isPending ? t('parentInviteSection.creating') : t('parentInviteSection.createLink')}
        </button>
      </div>
    </div>
  )
}
